// api/client.ts — тонкий fetch-клиент: JSON, multipart, auth (Bearer), авто-refresh,
// кастомные ошибки {status, body}. Singleton-стор для токенов см. stores/auth.ts.
import { useAuthStore } from '@/stores/auth';
import { useToast } from '@/stores/toast';

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message?: string) {
    super(message ?? `API ${status}`);
    this.name = 'ApiError';
  }
}

const JSON_RE = /^application\/json/i;

function baseUrl(): string {
  // dev: vite proxy /api → API_BASE_URL; prod: VITE_API_BASE_URL или тот же origin
  return (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
}

interface ReqOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  /** FormData — отправляется как multipart (для /media). */
  form?: FormData;
  /** Опустить заголовок auth (для /auth/login). */
  noAuth?: boolean;
  /** Не шлёт x-project (для запросов вне активного проекта). */
  noProject?: boolean;
  /** Дополнительные заголовки (x-project для чужого проекта и т.п.). */
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

async function request<T = unknown>(path: string, opts: ReqOpts = {}): Promise<T> {
  const url = new URL(`${baseUrl()}${path}`, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }
  const headers: Record<string, string> = {};
  if (!opts.noAuth) {
    const auth = useAuthStore();
    const token = auth.accessToken;
    if (token) headers.authorization = `Bearer ${token}`;
    // публичные роуты (submissions) дополнительно шлют slug — handled callers; тут по умолчанию auth
    if (!token) headers['x-tenant-slug'] = auth.tenantSlug ?? '';
    // активный проект (мультисайтовость): site/pages/posts/... фильтруются по нему
    if (!opts.noProject && auth.projectSlug) headers['x-project'] = auth.projectSlug;
    if (opts.headers) Object.assign(headers, opts.headers);
  }
  let payload: BodyInit | undefined;
  if (opts.form) {
    payload = opts.form; // multipart — Content-type ставит браузер
  } else if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
    payload = JSON.stringify(opts.body);
  }
  let res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers, body: payload, signal: opts.signal,
    credentials: 'include',
  });
  // 401 → попробовать refresh один раз, затем повторить запрос
  if (res.status === 401 && !opts.noAuth) {
    const auth = useAuthStore();
    const refreshed = await auth.tryRefresh().catch(() => false);
    if (refreshed) {
      headers.authorization = `Bearer ${auth.accessToken}`;
      res = await fetch(url, { method: opts.method ?? 'GET', headers, body: payload, credentials: 'include', signal: opts.signal });
    } else {
      auth.logout('session_expired');
    }
  }
  const text = await res.text();
  let parsed: unknown = null;
  if (text && JSON_RE.test(res.headers.get('content-type') ?? '')) {
    try { parsed = JSON.parse(text); } catch { parsed = text; } // мусорный body под json content-type — отдаём как текст
  } else {
    parsed = text || null;
  }
  if (!res.ok) {
    // показать тост для 5xx и непредвиденных 4xx. Контрактные 4xx (409/422/404/403) и 401
    // обрабатывает вызывающий код (401 — auth: refresh/logout или «неверный пароль»).
    if (res.status >= 500 || (res.status >= 400 && res.status !== 409 && res.status !== 422 && res.status !== 404 && res.status !== 403 && res.status !== 401)) {
      const toast = useToast();
      toast.error(((parsed as { error?: string } | null)?.error) ?? `Ошибка ${res.status}`);
    }
    throw new ApiError(res.status, parsed, (parsed as { message?: string } | null)?.message);
  }
  return parsed as T;
}

export const api = {
  get: <T = unknown>(path: string, query?: ReqOpts['query']) => request<T>(path, { query }),
  post: <T = unknown>(path: string, body?: unknown, o?: Partial<ReqOpts>) => request<T>(path, { method: 'POST', body, ...o }),
  put: <T = unknown>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T = unknown>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T = unknown>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T = unknown>(path: string, form: FormData) => request<T>(path, { method: 'POST', form }),
  raw: request,
};
